/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Button, IModuleProps, INodeProps } from '@msdyn365-commerce-modules/utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import AddressSearch, { IFormattedAddressDetails } from '../../components/citta-address-search/citta-address-search';
import { GetCookie } from '../../shared-utils/cookie-utilities';
import { submitStylingEnquiryFormAction, SubmitStylingEnquiryFormDataActionInput } from './../../data-actions/styling-enquiry/submit-styling-enquiry-form-data';
import { ICittaEnquirenowData } from './citta-enquirenow.data';
import { ICittaEnquirenowConfig, ICittaEnquirenowProps } from './citta-enquirenow.props.autogenerated';

export interface ICittaEnquirenowForm {
    formWrapper: INodeProps;
    formContainer: INodeProps;
    name: React.ReactNode;
    emailAddress: React.ReactNode;
    phoneNumber: React.ReactNode;
    streetAddress: React.ReactNode;
    errorMessage: React.ReactNode;
}

export interface ICittaEnquireNowViewProps extends ICittaEnquirenowProps<ICittaEnquirenowData> {
    enquireNowContainer: IModuleProps;
    enquireNowHeader: React.ReactNode;
    enquireNowForm: ICittaEnquirenowForm;
    consultationType: React.ReactNode;
    storePickerValue: React.ReactNode;
    stylingHomeCheckedItems: React.ReactNode;
    stylingDayCheckedItems: React.ReactNode;
    stylingTimeCheckedItems: React.ReactNode;
    specificTimeMessage: React.ReactNode;
    pinterestMessage: React.ReactNode;
    subscribeToMailingList: React.ReactNode;
    submitButton: React.ReactNode;
    responseMessage: React.ReactNode;
}

export interface ICittaEnquireNowFormState {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    streetAddress: string;
    suburb: string;
    cityState: string;
    postCode: string;
    manualAddress: boolean;
    allowAddressSearch: boolean;
    storePickerValue: string;
    stylingHomeCheckedItems: string[];
    stylingHelpMessage: string;
    stylingDayCheckedItems: string[];
    stylingTimeCheckedItems: string[];
    specificTimeMessage: string;
    pinterestMessage: string;
    subscribeToMailingList: number;
    fNError: boolean;
    lNError: boolean;
    eAError: boolean;
    pHError: boolean;
    sAError: boolean;
    suError: boolean;
    cSError: boolean;
    pCError: boolean;
    cTError: boolean;
    sHError: boolean;
    sHMError: boolean;
    sDError: boolean;
    sTError: boolean;
    reqError: boolean;
    eAValError: boolean;
    responseReceived: boolean;
}

/**
 *
 * CittaEnquirenow component
 * @extends {React.PureComponent<ICittaEnquirenowProps<ICittaEnquirenowData>>}
 */
@observer
class CittaEnquirenow extends React.PureComponent<ICittaEnquirenowProps<ICittaEnquirenowData>, ICittaEnquireNowFormState> {
    public state: ICittaEnquireNowFormState = {
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        streetAddress: '',
        suburb: '',
        cityState: '',
        postCode: '',
        manualAddress: false,
        allowAddressSearch: true,
        storePickerValue: '',
        stylingHomeCheckedItems: [],
        stylingHelpMessage: '',
        stylingDayCheckedItems: [],
        stylingTimeCheckedItems: [],
        specificTimeMessage: '',
        pinterestMessage: '',
        subscribeToMailingList: 0,
        fNError: false,
        lNError: false,
        eAError: false,
        pHError: false,
        sAError: false,
        suError: false,
        cSError: false,
        pCError: false,
        cTError: false,
        sHError: false,
        sHMError: false,
        sDError: false,
        sTError: false,
        reqError: false,
        eAValError: false,
        responseReceived: false
    };

    private _FirstNameInputRef: React.RefObject<HTMLInputElement>;
    private _LastNameInputRef: React.RefObject<HTMLInputElement>;
    private _EmailAddressInputRef: React.RefObject<HTMLInputElement>;
    private _PhoneNumberInputRef: React.RefObject<HTMLInputElement>;
    private _StreetAddressInputRef: React.RefObject<HTMLInputElement>;
    private _SuburbInputRef: React.RefObject<HTMLInputElement>;
    private _CityStateInputRef: React.RefObject<HTMLInputElement>;
    private _PostCodeInputRef: React.RefObject<HTMLInputElement>;
    private _storePickerSelectRef: React.RefObject<HTMLSelectElement>;
    private _stylingHelpInputRef: React.RefObject<HTMLTextAreaElement>;
    private _specificTimeInputRef: React.RefObject<HTMLTextAreaElement>;
    private _pinterestInputRef: React.RefObject<HTMLTextAreaElement>;
    private _SearchAddressRef: React.RefObject<AddressSearch>;

    private firstNameValue: boolean = false;
    private lastNameValue: boolean = false;
    private emailAddressValue: boolean = false;
    private phoneNumberValue: boolean = false;
    private consultationTypeValue: boolean = false;
    private stylingHelpTextValue: boolean = false;

    private requiredErrorMessage: string | undefined;
    private eAValidationErrorMessage: string | undefined;
    private response: boolean = false;

    private _cookieName: string = this.props.context.app.config.marketPreferenceCookieName || '_msdyn365___market_preference';

    constructor(props: ICittaEnquirenowProps<ICittaEnquirenowData>) {
        super(props);
        this._FirstNameInputRef = React.createRef();
        this._LastNameInputRef = React.createRef();
        this._EmailAddressInputRef = React.createRef();
        this._PhoneNumberInputRef = React.createRef();
        this._StreetAddressInputRef = React.createRef();
        this._SuburbInputRef = React.createRef();
        this._CityStateInputRef = React.createRef();
        this._PostCodeInputRef = React.createRef();
        this._storePickerSelectRef = React.createRef();
        this._stylingHelpInputRef = React.createRef();
        this._specificTimeInputRef = React.createRef();
        this._pinterestInputRef = React.createRef();
        this._SearchAddressRef = React.createRef();
        this._handleSend = this._handleSend.bind(this);
        this._handleSearchAddressSelection = this._handleSearchAddressSelection.bind(this);
        this._toggleManualAddressInput = this._toggleManualAddressInput.bind(this);
    }

    public componentDidMount(): void {
        const market = GetCookie(this.props.context.request.cookies, this._cookieName);
        if (market && market === 'AUS') {
            this.setState({
                allowAddressSearch: false
            });
        }
    }

    // tslint:disable-next-line: max-func-body-length
    public render(): JSX.Element | null {
        const { config, slots } = this.props;

        if (!slots) {
            return null;
        }

        const viewProps: ICittaEnquireNowViewProps = {
            ...(this.props as ICittaEnquirenowProps<ICittaEnquirenowData>),
            enquireNowContainer: {
                moduleProps: this.props,
                className: 'enquire-now'
            },
            enquireNowHeader: this._renderHeader(),
            enquireNowForm: this._renderForm(),
            consultationType: this._renderConsultationType(config),
            storePickerValue: this._renderStorePicker(config),
            stylingHomeCheckedItems: this._renderHomeStyling(config),
            stylingDayCheckedItems: this._renderBookStylingByDay(config),
            stylingTimeCheckedItems: this._renderBookStylingByTime(config),
            specificTimeMessage: this._renderSpecificTime(config),
            pinterestMessage: this._renderPinterestBoard(config),
            subscribeToMailingList: this._renderSubscribeCheck(config),
            submitButton: this._renderSubmitButton(),
            responseMessage: this._renderResponseMessage()
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _renderHeader(): React.ReactNode {
        return (
            <p className='enquire-now-header'>{this.props.config.enquireNowHeader}</p>
        );
    }

    private _renderForm(): ICittaEnquirenowForm {
        return {
            formWrapper: {
                className: 'enquire-now-form u-mbot30',
            },
            formContainer: {
                className: 'enquire-now-form-div',
            },
            name: this._renderFirstandLastName(),
            emailAddress: this._renderEmailAddress(),
            phoneNumber: this._renderPhoneNumber(),
            streetAddress: this._renderAddress(),
            errorMessage: this._renderErrorMessage()
        };
    }

    private _renderFirstandLastName(): React.ReactNode {
        return (
            <div className='first-and-last-name-input'>
                <div className='name-input-error'>
                    <span className='first-name-input-error'>
                        {this.state.fNError ? this._renderAlert('first-name-input') : ''}
                    </span>
                    <span className='last-name-input-error'>
                        {this.state.lNError ? this._renderAlert('last-name-input') : ''}
                    </span>
                </div>
                <div className='first-name-input'>
                    <input
                        ref={this._FirstNameInputRef}
                        type='text'
                        onChange={this.handleChange('firstName')}
                        aria-label='First Name'
                        className='first-name-input-text'
                        placeholder={this.props.resources.firstNamePlaceholderText}
                        value={this.state.firstName}
                        required
                    />
                </div>
                <div className='last-name-input'>
                    <input
                        ref={this._LastNameInputRef}
                        type='text'
                        onChange={this.handleChange('lastName')}
                        aria-label='Last Name'
                        className='last-name-input-text'
                        placeholder={this.props.resources.lastNamePlaceholderText}
                        value={this.state.lastName}
                        required
                    />
                </div>
            </div>
        );
    }

    private _renderEmailAddress(): React.ReactNode {
        return (
            <div className='email-address-input'>
                {this.state.eAError || this.state.eAValError ? this._renderAlert('email-address-input') : ''}
                <input
                    ref={this._EmailAddressInputRef}
                    type='email'
                    onChange={this.handleChange('emailAddress')}
                    aria-label='Email Address'
                    className='email-address-input-text'
                    placeholder={this.props.resources.emailAddressPlaceholderText}
                    value={this.state.emailAddress}
                    required
                />
            </div>
        );
    }

    private _renderPhoneNumber(): React.ReactNode {
        return (
            <div className='phone-number-input'>
                {this.state.pHError ? this._renderAlert('phone-number-input') : ''}
                <input
                    ref={this._PhoneNumberInputRef}
                    type='tel'
                    onChange={this.handleChange('phoneNumber')}
                    aria-label='Phone Number'
                    className='phone-number-input-text'
                    placeholder={this.props.resources.phoneNumberPlaceholderText}
                    value={this.state.phoneNumber}
                    required
                />
            </div>
        );
    }

    private _renderAddress(): React.ReactNode {
        const showManualAddress = this.state.manualAddress || !this.state.allowAddressSearch;
        return (
            <div className='manual-search-toggle'>
                {showManualAddress && <div className='manual-input'>
                    <div className='street-address-input'>
                        {this.state.sAError ? this._renderAlert('street-address-input') : ''}
                        <input
                            ref={this._StreetAddressInputRef}
                            type='text'
                            onChange={this.handleChange('streetAddress')}
                            className='street-address-input-text'
                            placeholder={this.props.resources.streetAddressPlaceholderText}
                            value={this.state.streetAddress}
                            required
                        />
                    </div>
                    <div className='suburb-input'>
                        {this.state.suError ? this._renderAlert('suburb-input') : ''}
                        <input
                            ref={this._SuburbInputRef}
                            type='text'
                            onChange={this.handleChange('suburb')}
                            className='suburb-input-text'
                            placeholder={this.props.resources.suburbPlaceholderText}
                            value={this.state.suburb}
                            required
                        />
                    </div>
                    <div className='city-state-input'>
                        {this.state.cSError ? this._renderAlert('city-state-input') : ''}
                        <input
                            ref={this._CityStateInputRef}
                            type='text'
                            onChange={this.handleChange('cityState')}
                            className='city-state-input-text'
                            placeholder={this.props.resources.cityStatePlaceholderText}
                            value={this.state.cityState}
                            required
                        />
                    </div>
                    <div className='postcode-input'>
                        {this.state.pCError ? this._renderAlert('postcode-input') : ''}
                        <input
                            ref={this._PostCodeInputRef}
                            type='tel'
                            onChange={this.handleChange('postCode')}
                            className='postcode-input-text'
                            pattern='[0-9]*'
                            max='9999'
                            placeholder={this.props.resources.postCodePlaceholderText}
                            value={this.state.postCode}
                            required
                        />
                    </div>
                </div>}
                {!this.state.manualAddress &&
                    this.state.sAError ? this._renderAlert('street-address-input') : ''}
                {this.state.allowAddressSearch &&
                    <AddressSearch
                        ref={this._SearchAddressRef}
                        context={this.props.context}
                        manualAddress={this.state.manualAddress}
                        addressSelectedCallback={this._handleSearchAddressSelection}
                        toggleManualAddressCallback={this._toggleManualAddressInput}
                    />}
            </div>
        );
    }

    private _renderResponseMessage(): React.ReactNode {
        if (this.state.responseReceived && this.response === true) {
            return (
                <div>
                    <div className='response-message-header'>{this.props.resources.responseThankyouMessage}</div>
                    <p className='response-message-content'>{this.props.resources.responseSuccessMessage}</p>
                </div>
            );
        } else {
            return null;
        }
    }

    private _renderErrorMessage(): React.ReactNode {
        if (this.state.responseReceived && this.response === false) {
            return (
                <div className='response-message'>
                    <p className='response-error-message-content'>{this.props.resources.responseErrorMessage}</p>
                </div>
            );
        } else {
            return null;
        }
    }

    private _renderConsultationType(config: ICittaEnquirenowConfig): React.ReactNode {
        const slots = this.props.slots;
        const contentSlots = slots.content ? slots.content : [];
        return (
            <div className='consultation-type u-mbot30'>
                {this.state.cTError ? this._renderAlert('consultation-type') : ''}
                <div className='checkWrap-heading'>{config.consultationTypeHeader}</div>
                <div className='consultation-type-info'>
                    {contentSlots.map((children: React.ReactNode, index: number) => {
                        return (
                            <div className='consultation-type-accordion' key={index}>
                                {children}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    private _renderStorePicker(config: ICittaEnquirenowConfig): React.ReactNode {
        const cittaStoresList: string = this.props.context.app.config.cittaStoresList || 'Città BLOC,000010|Città Grey Lynn,000012|Città Hamilton,000013|Città Merivale,000016|Città Wellington,000017|Città Wellington Thorndon,000018|Città Newmarket,000041|Città Takapuna,000044';
        const cittaStores: string[] = cittaStoresList.split('|');
        return (
            <div className='storepicker u-mbot30'>
                <div className='checkWrap-heading'>
                    {config.storePickerHeader}
                </div>
                <div className='storepicker-dropdown'>
                    <label id='store-picker__label' />
                    <select
                        ref={this._storePickerSelectRef}
                        className='store-picker-text'
                        value={this.state.storePickerValue}
                        onChange={this.onChangeSelect}
                        aria-label='Store Picker'
                        aria-labelledby='store-picker__label store-picker__error'
                    >
                        {
                            cittaStores.map((storeData, index) => {
                                const store = storeData.split(',');
                                return (
                                    <option value={store && store[1] || ''} key={index} selected={index === 0 && true || false} aria-selected={index === 0 && true || false}>{store && store[0] || ''}</option>
                                );
                            })
                        }
                    </select>
                </div>
            </div>
        );
    }

    private _renderHomeStyling(config: ICittaEnquirenowConfig): React.ReactNode {
        return (
            <div className='checkbox-section styling-home u-mbot30'>
                {this.state.sHError ? this._renderAlert('styling-home') : ''}
                <div className='checkWrap-heading'>
                    {config.stylingHomeHeader}
                </div>

                <div className='styling-home-options'>
                    {config && config.homeStylingOptions!.length && (
                        config.homeStylingOptions!.map((stylingOption: string, index: number) => {
                            return (
                                <div className='c-fauxCheckbox' key={index}>
                                    <input
                                        type='checkbox'
                                        aria-checked='false'
                                        id={stylingOption}
                                        value={stylingOption}
                                        onChange={this.stylingHomeOnChange(stylingOption)}
                                    />
                                    <label className='fauxRadio' htmlFor={stylingOption} />
                                    <label htmlFor={stylingOption}>{stylingOption}</label>
                                </div>
                            );
                        })
                    )
                    }
                </div>

                {/* tslint:disable-next-line: cyclomatic-complexity */}
                <div className='styling-help-text'>
                    {this.state.sHMError ? this._renderAlert('styling-home-text') : ''}
                    <label id='styling-help-message__label' />
                    <textarea
                        className='c-deftextArea'
                        name='text'
                        placeholder={this.props.resources.messagePlaceholderText}
                        ref={this._stylingHelpInputRef}
                        onChange={this.handleStylingHelpChange}
                        value={this.state.stylingHelpMessage}
                        aria-label='Message'
                        aria-labelledby='styling-help-message__label styling-help-message__error'
                    />
                </div>
            </div>
        );
    }

    private _renderBookStylingByDay(config: ICittaEnquirenowConfig): React.ReactNode {
        return (
            <div className='checkbox-section book-styling-day u-mbot30'>
                <div className='checkWrap-heading'>
                    {config.bookStylingDayHeader}
                </div>

                <div className='styling-day-options'>
                    {config && config.weekdaysOptions!.length && (
                        config.weekdaysOptions!.map((stylingDay: string, index: number) => {
                            return (
                                <div className='c-fauxCheckbox' key={index}>
                                    <input
                                        type='checkbox'
                                        aria-checked='false'
                                        id={stylingDay}
                                        value={stylingDay}
                                        onChange={this.stylingDayOnChange(stylingDay)}
                                    />
                                    <label className='fauxRadio' htmlFor={stylingDay} />
                                    <label htmlFor={stylingDay}>{stylingDay}</label>
                                </div>
                            );
                        })
                    )
                    }
                </div>

            </div>
        );
    }

    private _renderBookStylingByTime(config: ICittaEnquirenowConfig): React.ReactNode {
        return (
            <div className='checkbox-section book-styling-time u-mbot30'>
                <div className='checkWrap-heading'>
                    {config.bookStylingTimeHeader}
                </div>
                <div className='c-fauxCheckbox'>
                    <input
                        type='checkbox'
                        aria-checked='false'
                        id='notifyCheck8'
                        value='9am-11am'
                        onChange={this.stylingTimeOnChange('9am-11am')}
                    />
                    <label className='fauxRadio' htmlFor='notifyCheck8' />
                    <label htmlFor='notifyCheck8'>9am-11am</label>
                </div>
                <div className='c-fauxCheckbox'>
                    <input
                        type='checkbox'
                        aria-checked='false'
                        id='notifyCheck9'
                        value='11am-1pm'
                        onChange={this.stylingTimeOnChange('11am-1pm')}
                    />
                    <label className='fauxRadio' htmlFor='notifyCheck9' />
                    <label htmlFor='notifyCheck9'>11am-1pm</label>
                </div>
                <div className='c-fauxCheckbox'>
                    <input
                        type='checkbox'
                        aria-checked='false'
                        id='notifyCheck10'
                        value='1pm-3pm'
                        onChange={this.stylingTimeOnChange('1pm-3pm')}
                    />
                    <label className='fauxRadio' htmlFor='notifyCheck10' />
                    <label htmlFor='notifyCheck10'>1pm-3pm</label>
                </div>
                <div className='c-fauxCheckbox'>
                    <input
                        type='checkbox'
                        aria-checked='false'
                        id='notifyCheck11'
                        value='3pm-5pm'
                        onChange={this.stylingTimeOnChange('3pm-5pm')}
                    />
                    <label className='fauxRadio' htmlFor='notifyCheck11' />
                    <label htmlFor='notifyCheck11'>3pm-5pm</label>
                </div>
            </div>
        );
    }

    private _renderSpecificTime(config: ICittaEnquirenowConfig): React.ReactNode {
        return (
            <div className='specific-time u-mbot30'>
                <div className='checkWrap-heading'>
                    {config.stylingSpecificTimeHeader}
                </div>
                <div className='specific-time-text'>
                    <label id='specific-time-message__label' />
                    <textarea
                        className='c-deftextArea'
                        name='text'
                        placeholder={this.props.resources.specificDetailsPlaceholderText}
                        ref={this._specificTimeInputRef}
                        onChange={this.handleSpecificTimeChange}
                        value={this.state.specificTimeMessage}
                        aria-label='Time Message'
                        aria-labelledby='specific-time-message__label specific-time-message__error'
                    />
                </div>
            </div>
        );
    }

    private _renderPinterestBoard(config: ICittaEnquirenowConfig): React.ReactNode {
        return (
            <div className='pinterest-board u-mbot30'>
                <div className='checkWrap-heading'>
                    {config.pinterestBoardHeader}
                </div>
                <div className='pinterest-board-text'>
                    <label id='pinterest-message__label' />
                    <textarea
                        className='c-deftextArea'
                        name='text'
                        placeholder={this.props.resources.pasteLinksPlaceholderText}
                        ref={this._pinterestInputRef}
                        onChange={this.handlePinterestChange}
                        value={this.state.pinterestMessage}
                        aria-label='Pinterest Message'
                        aria-labelledby='pinterest-message__label pinterest-message__error'
                    />
                </div>
            </div>
        );
    }

    private _renderSubscribeCheck(config: ICittaEnquirenowConfig): React.ReactNode {
        return (
            <div className='checkbox-section subscribe-check u-mbot30'>
                <div className='c-fauxCheckbox'>
                    <input
                        type='checkbox'
                        aria-checked='false'
                        id='subscribeCheck'
                        value='subscribe'
                        onChange={this.subscribeCheckOnChange()}
                    />
                    <label className='fauxRadio' htmlFor='subscribeCheck' />
                    <label htmlFor='subscribeCheck'>Subscribe to our newsletter</label>
                </div>
            </div>
        );
    }

    private _renderSubmitButton(): React.ReactNode {
        return (
            <div className='enquiry-submit'>
                <Button
                    className='enquiry-submit-button'
                    title={this.props.resources.sendButtonLabel}
                    color='primary'
                    onClick={this._handleSend}
                    aria-label={this.props.resources.sendButtonLabel}
                >
                    {this.props.resources.sendButtonLabel}
                </Button>
            </div>
        );
    }

    /**
     * callback function for receiving address details from address search component
     */
    private _handleSearchAddressSelection(address: IFormattedAddressDetails): void {
        let streetAddressDetails = '';
        if (address) {
            streetAddressDetails += address.building_name ? `${address.building_name}` : '';
            streetAddressDetails += address.street_number ? `${streetAddressDetails.length > 0 ? ', ' : ''}${address.street_number}` : '';
            streetAddressDetails += address.street ? `${streetAddressDetails.length > 0 ? ' ' : ''}${address.street}` : '';
        }
        this.setState({
            streetAddress: streetAddressDetails,
            suburb: address && address.suburb ? address.suburb : '',
            cityState: address && address.city ? address.city : '',
            postCode: address && address.postcode ? address.postcode : '',
            sAError: false,
            suError: false,
            cSError: false,
            pCError: false
        });
    }

    private _toggleManualAddressInput(): void {
        this.setState({
            manualAddress: !this.state.manualAddress,
            streetAddress: '',
            suburb: '',
            cityState: '',
            postCode: ''
        });
    }

    private onChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ storePickerValue: e.target.value || '' });
    };

    private stylingHomeOnChange = (value: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        let stylingHomeCheckedItems = this.state.stylingHomeCheckedItems;
        if (isChecked) {
            stylingHomeCheckedItems.push(value);
        } else {
            stylingHomeCheckedItems = stylingHomeCheckedItems.filter(item => item !== value);
        }
        this.setState({ stylingHomeCheckedItems: stylingHomeCheckedItems, sHError: false });
    }

    private handleStylingHelpChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ stylingHelpMessage: e.target.value || '', sHMError: false });
    };

    private handleSpecificTimeChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ specificTimeMessage: e.target.value || '' });
    };

    private handlePinterestChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({ pinterestMessage: e.target.value || '' });
    };

    private stylingDayOnChange = (value: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        let stylingDayCheckedItems = this.state.stylingDayCheckedItems;
        if (isChecked) {
            stylingDayCheckedItems.push(value);
        } else {
            stylingDayCheckedItems = stylingDayCheckedItems.filter(item => item !== value);
        }
        this.setState({ stylingDayCheckedItems: stylingDayCheckedItems, sDError: false });
    }

    private stylingTimeOnChange = (value: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        let stylingTimeCheckedItems = this.state.stylingTimeCheckedItems;
        if (isChecked) {
            stylingTimeCheckedItems.push(value);
        } else {
            stylingTimeCheckedItems = stylingTimeCheckedItems.filter(item => item !== value);
        }
        this.setState({ stylingTimeCheckedItems: stylingTimeCheckedItems, sTError: false });
    }

    private subscribeCheckOnChange = () => (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        this.setState({ subscribeToMailingList: isChecked ? 1 : 0 });
    }

    private handleChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        switch (name) {
            case 'firstName': {
                this.setState({ firstName: value, reqError: false, fNError: false, eAValError: false });
                break;
            }
            case 'lastName': {
                this.setState({ lastName: value, reqError: false, lNError: false, eAValError: false });
                break;
            }
            case 'emailAddress': {
                this.setState({ emailAddress: value, reqError: false, eAError: false, eAValError: false });
                break;
            }
            case 'phoneNumber': {
                this.setState({ phoneNumber: value, reqError: false, pHError: false, eAValError: false });
                break;
            }
            case 'streetAddress': {
                this.setState({ streetAddress: value, reqError: false, sAError: false, eAValError: false });
                break;
            }
            case 'suburb': {
                this.setState({ suburb: value, reqError: false, suError: false, eAValError: false });
                break;
            }
            case 'cityState': {
                this.setState({ cityState: value, reqError: false, cSError: false, eAValError: false });
                break;
            }
            case 'postCode': {
                this.setState({ postCode: value, reqError: false, pCError: false, eAValError: false });
                break;
            }
            case 'country': {
                this.setState({ reqError: false, eAValError: false });
                break;
            }
            case 'privacyPolicy': {
                this.setState({ reqError: false, eAValError: false });
                break;
            }
            default: {
                console.log('No input box changes');
                this.setState({ reqError: false, eAValError: false });
            }
        }
    };

    private isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1, 3}\.[0-9]{1, 3}\.[0-9]{1, 3}\.[0-9]{1, 3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private _renderAlert(className: string): JSX.Element {
        const inputBoxErrors =
            (this.state.fNError ||
                this.state.lNError ||
                this.state.eAError ||
                this.state.pHError ||
                this.state.sAError ||
                this.state.suError ||
                this.state.cSError ||
                this.state.pCError ||
                this.state.cTError ||
                this.state.sHError ||
                this.state.sHMError ||
                this.state.sDError ||
                this.state.sTError);
        if (inputBoxErrors) {
            this.requiredErrorMessage = this.props.resources.requiredTextErrorMessage;
        }
        if (this.state.emailAddress !== '' && this.state.eAValError) {
            this.eAValidationErrorMessage = this.props.resources.eAValidationErrorMessage;
        }
        const reqError = this.state.reqError;
        const eAValError = this.state.eAValError;
        return (
            <>
                {reqError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.requiredErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.requiredErrorMessage}
                            </>
                        }
                    </span>
                )}
                {eAValError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.eAValidationErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.eAValidationErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    // tslint:disable-next-line: max-func-body-length
    private async _handleSend(): Promise<void> {
        this.firstNameValue = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? true : false;
        this.lastNameValue = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? true : false;
        this.emailAddressValue = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? true : false;
        this.phoneNumberValue = (this._PhoneNumberInputRef && this._PhoneNumberInputRef.current && this._PhoneNumberInputRef.current.value) ? true : false;
        this.consultationTypeValue = this.props.data.accordionExpandedState.result!.radioButtonText ? true : false;
        const otherChecked = this.state.stylingHomeCheckedItems.includes('Other') || this.state.stylingHomeCheckedItems.includes('other');
        this.stylingHelpTextValue = (this.state.stylingHelpMessage) ? true : false;

        const fN = (this._FirstNameInputRef && this._FirstNameInputRef.current && this._FirstNameInputRef.current.value) ? this._FirstNameInputRef.current.value : '';
        const lN = (this._LastNameInputRef && this._LastNameInputRef.current && this._LastNameInputRef.current.value) ? this._LastNameInputRef.current.value : '';
        const eA = (this._EmailAddressInputRef && this._EmailAddressInputRef.current && this._EmailAddressInputRef.current.value) ? this._EmailAddressInputRef.current.value : '';
        const pH = (this._PhoneNumberInputRef && this._PhoneNumberInputRef.current && this._PhoneNumberInputRef.current.value) ? this._PhoneNumberInputRef.current.value : '';
        const sA = (this.state.streetAddress && this.state.streetAddress.length > 0) ? this.state.streetAddress : '';
        const sU = (this.state.suburb && this.state.suburb.length > 0) ? this.state.suburb : '';
        const cS = (this.state.cityState && this.state.cityState.length > 0) ? this.state.cityState : '';
        const pC = (this.state.postCode && this.state.postCode.length > 0) ? this.state.postCode : '';
        const aD = `${sA} ${sU} ${cS} ${pC}`;
        const sN = (this._storePickerSelectRef && this._storePickerSelectRef.current && this._storePickerSelectRef.current.selectedOptions[0].innerText) ? this._storePickerSelectRef.current.selectedOptions[0].innerText : '';
        const hA = this.state.stylingHomeCheckedItems.toString();
        const sM = this.state.stylingHelpMessage;
        const sD = this.state.stylingDayCheckedItems.toString();
        const sT = this.state.stylingTimeCheckedItems.toString();
        const sDT = this.state.specificTimeMessage;
        const pT = this.state.pinterestMessage;
        const cT = (this.props.data.accordionExpandedState && this.props.data.accordionExpandedState.result && this.props.data.accordionExpandedState.result.radioButtonText) ? this.props.data.accordionExpandedState.result.radioButtonText : '';
        const cI = (this._storePickerSelectRef.current!.value);
        const eAVal = this._EmailAddressInputRef.current!.value;
        const sub = this.state.subscribeToMailingList === 1 ? 1 : 0;

        if (!this.firstNameValue) {
            this.setState({
                fNError: true,
                reqError: true
            });
        } else if (!this.lastNameValue) {
            this.setState({
                lNError: true,
                reqError: true
            });
        } else if (!this.emailAddressValue) {
            this.setState({
                eAError: true,
                reqError: true
            });
        } else if (!this.isEmailValid(eAVal)) {
            this.setState({
                eAValError: true
            });
        } else if (!this.phoneNumberValue) {
            this.setState({
                pHError: true,
                reqError: true
            });
        } else if (!this.state.streetAddress) {
            this.setState({
                sAError: true,
                reqError: true
            });
        } else if (!this.state.suburb) {
            this.setState({
                suError: true,
                reqError: true
            });
        } else if (!this.state.cityState) {
            this.setState({
                cSError: true,
                reqError: true
            });
        } else if (!this.state.postCode) {
            this.setState({
                pCError: true,
                reqError: true
            });
        } else if (!this.consultationTypeValue) {
            this.setState({
                cTError: true,
                reqError: true
            });
        } else if (this.state.stylingHomeCheckedItems.length < 1) {
            this.setState({
                sHError: true,
                reqError: true
            });
        } else if (!this.stylingHelpTextValue && otherChecked) {
            this.setState({
                sHMError: true,
                reqError: true
            });
        } else {
            try {
                // tslint:disable-next-line: cyclomatic-complexity
                const result = await submitStylingEnquiryFormAction(new SubmitStylingEnquiryFormDataActionInput(fN, lN, eA, pH, aD, sN, hA, sM, sD, sT, sDT, pT, cT, cI, sub), this.props.context.actionContext);
                this.response = result.success;
                if (this.response === true) {
                    this.setState({
                        firstName: '',
                        lastName: '',
                        emailAddress: '',
                        phoneNumber: '',
                        streetAddress: '',
                        suburb: '',
                        cityState: '',
                        postCode: '',
                        storePickerValue: '',
                        stylingHomeCheckedItems: [],
                        stylingHelpMessage: '',
                        stylingDayCheckedItems: [],
                        stylingTimeCheckedItems: [],
                        specificTimeMessage: '',
                        pinterestMessage: '',
                        responseReceived: true,
                        subscribeToMailingList: 0
                    });
                    if (this._SearchAddressRef.current) {
                        this._SearchAddressRef.current.clearAddress();
                    }
                } else {
                    this.setState({ responseReceived: true });
                }
            } catch {
                console.log('Please fill the form!');
            }
        }
    }
}

export default CittaEnquirenow;